import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Stack, Tooltip, Typography, useTheme } from '@mui/material';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined';
import PlagiarismOutlinedIcon from '@mui/icons-material/PlagiarismOutlined';
import { PulseLoader } from 'react-spinners';
import { AdvisorMode, Delete, Download, Share, ShareProject } from '@brainpool/icon-pack';
import { ResourceType, usePresignedDownload } from '../../hooks/usePreSignedDownload';
import { getGetWorkflowStatus, handleGoToProject } from '../../utils/commonFunctions';
import { WorkflowStatus } from '../../types/WorkflowState';
import { ModalProps } from '../../types/ModalTypes';
import { UserRole } from '../../types/User';

export interface HeaderActionProps {
  onOpenModal: (type: ModalProps['type']) => void;
  onRefresh: () => void;
  onAdvisorMode: () => void;
  report_path?: string;
  id: string;
  name: string;
  status: WorkflowStatus;
  role: UserRole;
}

export function HeaderAction({
  onAdvisorMode,
  onOpenModal,
  onRefresh,
  report_path,
  id,
  name,
  status,
  role,
}: HeaderActionProps) {
  const navigate = useNavigate();
  const theme = useTheme();

  const { getPresignedDownloadUrl, loading, data: presignedData } = usePresignedDownload();

  const [isWorkflowCompleted, setIsWorkflowCompleted] = useState<boolean>(true);

  useEffect(() => {
    if (presignedData?.getPreSignedDownloadUrl?.url) {
      window.open(presignedData.getPreSignedDownloadUrl.url, '_blank', 'noopener,noreferrer');
    }
  }, [presignedData]);

  useEffect(() => {
    setIsWorkflowCompleted(status ? getGetWorkflowStatus(status) : false);
  }, [status]);

  const handleDownloadReport = () => {
    if (report_path && id) {
      getPresignedDownloadUrl(report_path, id, ResourceType.REPORT);
    }
  };

  return (
    <Stack direction="row" justifyContent="flex-end" data-testid="header-action-buttons">
      <Button
        variant="text"
        onClick={onRefresh}
        aria-label="refresh button"
        sx={{ p: '0.75rem 0.5rem !important', minWidth: '48px' }}
      >
        <Tooltip title="Refresh project">
          <RefreshOutlinedIcon
            fontSize="medium"
            sx={{ fontSize: 16, color: theme.palette.text.primary }}
          />
        </Tooltip>
      </Button>
      <Button
        onClick={() => onOpenModal('delete')}
        sx={{ p: '0.75rem 0.5rem !important', minWidth: '48px' }}
        aria-label="delete button"
        data-testid="delete-project-button"
      >
        <Tooltip title="Delete project">
          <Delete size={16} fill={theme.palette.text.primary} />
        </Tooltip>
      </Button>
      {role === 'Owner' && (
        <>
          <Button
            onClick={() => onOpenModal('share')}
            sx={{ p: '0.75rem 0.5rem !important', minWidth: '48px' }}
            aria-label="share button"
            data-testid="share-projects-button"
          >
            <Tooltip title={role}>
              <Share size={16} fill={theme.palette.text.primary} />
            </Tooltip>
          </Button>
          <Button
            onClick={() => onOpenModal('permissions')}
            sx={{ p: '0.75rem 0.5rem !important', minWidth: '48px' }}
            aria-label="permissions button"
            data-testid="project-permissions-button"
          >
            <Tooltip title="Permissions">
              <ShareProject size={16} fill={theme.palette.text.primary} />
            </Tooltip>
          </Button>
        </>
      )}

      {name && id && (
        <Button
          onClick={() => {
            handleGoToProject(id, name, 'documents', navigate);
          }}
          sx={{ p: '0.75rem 0.5rem !important', minWidth: '48px' }}
          aria-label="documents button"
          data-testid="documents-button"
        >
          <Tooltip title="View documents">
            <FolderOutlinedIcon
              fontSize="medium"
              sx={{ fontSize: 16, color: theme.palette.text.primary }}
            />
          </Tooltip>
        </Button>
      )}

      <Button
        variant="contained"
        onClick={handleDownloadReport}
        disabled={!report_path || loading}
        data-testid="download-report-button"
      >
        <Tooltip
          title={
            !report_path || loading
              ? 'A report will be available once the full data room has been uploaded and a due diligence has ran.'
              : ''
          }
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography sx={{ pr: 2 }}>Download Report</Typography>
            {loading ? (
              <PulseLoader size={8} />
            ) : (
              <Download
                size={16}
                fill={
                  status !== 'report_completed'
                    ? theme.palette.info.dark
                    : theme.palette.text.primary
                }
              />
            )}
          </Box>
        </Tooltip>
      </Button>
      <Button
        onClick={onAdvisorMode}
        aria-label="advisor mode button"
        variant="contained"
        sx={{ color: theme.palette.primary.dark, background: theme.palette.info.main }}
        disabled={!role || !id}
      >
        <Typography sx={{ pr: 2 }}>Advisor Mode </Typography>
        <AdvisorMode size={16} fill={theme.palette.primary.dark} />
      </Button>
      {role === 'Owner' && (
        <Button
          onClick={() => onOpenModal('dueDiligence')}
          data-testid="due_diligence-button"
          aria-label="run full due diligence button"
          variant="contained"
          sx={{ color: theme.palette.common.white, background: theme.palette.success.dark }}
          disabled={!isWorkflowCompleted}
        >
          <Typography sx={{ pr: 2 }}>
            {status === 'duediligence_completed' || status === 'report_completed'
              ? ' Re-run Full Due Diligence'
              : 'Run Full Due Diligence'}
          </Typography>
          <PlagiarismOutlinedIcon
            fontSize="medium"
            sx={{
              fontSize: 18,
              color: isWorkflowCompleted ? theme.palette.common.white : theme.palette.info.dark,
            }}
          />
        </Button>
      )}
    </Stack>
  );
}
